import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  fetchWholesaleRequests,
  approveWholesaleRequest,
  rejectWholesaleRequest,
  fetchWholesaleTiers,
  setCustomerTier,
  WholesaleRequest,
  WholesaleRequestStatus,
  WholesaleTier,
} from "../api/wholesaleApi";

const STATUS_COLORS: Record<WholesaleRequestStatus, "warning" | "success" | "error"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const WholesaleRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<WholesaleRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<WholesaleRequestStatus | "all">("pending");
  const [actingId, setActingId] = useState<number | null>(null);
  const [tiers, setTiers] = useState<WholesaleTier[]>([]);
  // Per-pending-row tier choice for approval (keyed by request id).
  const [approveTier, setApproveTier] = useState<Record<number, number>>({});

  const defaultTierId = useMemo(
    () => tiers.find((t) => t.is_default)?.id ?? tiers[0]?.id ?? null,
    [tiers]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWholesaleRequests(filter === "all" ? undefined : filter);
      setRequests(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load wholesale requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchWholesaleTiers()
      .then(setTiers)
      .catch((err) => console.error("Failed to load tiers", err));
  }, []);

  const handleApprove = async (req: WholesaleRequest) => {
    setActingId(req.id);
    try {
      const tierId = approveTier[req.id] ?? defaultTierId ?? undefined;
      await approveWholesaleRequest(req.id, tierId ?? undefined);
      toast.success(`${req.business_name} approved for wholesale`);
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Failed to approve request");
    } finally {
      setActingId(null);
    }
  };

  const handleAssignTier = async (req: WholesaleRequest, tierId: number) => {
    setActingId(req.id);
    try {
      await setCustomerTier(req.user_id, tierId);
      toast.success(`Tier updated for ${req.business_name}`);
      await load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to assign tier");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (req: WholesaleRequest) => {
    setActingId(req.id);
    try {
      await rejectWholesaleRequest(req.id);
      toast.info(`${req.business_name} rejected`);
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Failed to reject request");
    } finally {
      setActingId(null);
    }
  };

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status === "pending").length,
    [requests]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Wholesale Requests
        </Typography>
        {filter === "pending" && pendingCount > 0 && (
          <Chip label={`${pendingCount} pending`} color="warning" />
        )}
      </Stack>

      <ToggleButtonGroup
        value={filter}
        exclusive
        size="small"
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="pending">Pending</ToggleButton>
        <ToggleButton value="approved">Approved</ToggleButton>
        <ToggleButton value="rejected">Rejected</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          No wholesale requests {filter !== "all" ? `with status "${filter}"` : ""}.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {requests.map((req) => (
            <Card key={req.id} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6">{req.business_name}</Typography>
                      <Chip
                        size="small"
                        label={req.status}
                        color={STATUS_COLORS[req.status]}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Account: {req.user_name} ({req.user_email})
                    </Typography>
                    {(req.first_name || req.last_name || req.contact_name) && (
                      <Typography variant="body2">
                        Contact: {[req.first_name, req.last_name].filter(Boolean).join(" ") || req.contact_name}
                      </Typography>
                    )}
                    {req.phone && <Typography variant="body2">Phone: {req.phone}</Typography>}
                    {req.website && (
                      <Typography variant="body2">Website: {req.website}</Typography>
                    )}
                    {req.business_type && (
                      <Typography variant="body2">Type: {req.business_type}</Typography>
                    )}
                    {req.address && <Typography variant="body2">Address: {req.address}</Typography>}
                    {req.heard_about && (
                      <Typography variant="body2">Heard about us: {req.heard_about}</Typography>
                    )}
                    {/* legacy fields (older applications) */}
                    {req.business_number && (
                      <Typography variant="body2">Business #: {req.business_number}</Typography>
                    )}
                    {req.message && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                        "{req.message}"
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Submitted {new Date(req.created_at).toLocaleString()}
                    </Typography>
                  </Box>

                  {req.status === "pending" && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                      <Stack spacing={1} justifyContent="center" sx={{ minWidth: 170 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Approve as tier</InputLabel>
                          <Select
                            label="Approve as tier"
                            value={approveTier[req.id] ?? defaultTierId ?? ""}
                            onChange={(e) =>
                              setApproveTier((s) => ({ ...s, [req.id]: Number(e.target.value) }))
                            }
                          >
                            {tiers.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name}
                                {t.is_default ? " (default)" : ` · ${Number(t.discount_pct ?? 0)}% off`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="success"
                          disabled={actingId === req.id}
                          onClick={() => handleApprove(req)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={actingId === req.id}
                          onClick={() => handleReject(req)}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </>
                  )}

                  {req.status === "approved" && req.user_is_wholesale && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                      <Stack spacing={1} justifyContent="center" sx={{ minWidth: 170 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Pricing tier</InputLabel>
                          <Select
                            label="Pricing tier"
                            value={req.user_tier_id ?? defaultTierId ?? ""}
                            disabled={actingId === req.id || !tiers.length}
                            onChange={(e) => handleAssignTier(req, Number(e.target.value))}
                          >
                            {tiers.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name}
                                {t.is_default ? " (default)" : ` · ${Number(t.discount_pct ?? 0)}% off`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default WholesaleRequestsPage;
